/**
 * Login.jsx
 * src/componentes/user/UserLogin.jsx
 *
 * User-facing login page.
 * After login redirects to: /  (or the page they tried to visit)
 *
 * API expected:
 *   POST /users.php?action=login
 *   Body: { email, password }
 *   Response: { status: true, data: { user_id, name, email, mobile, telegram_chat_id } }
 */

import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { apiFetch } from "../helpers";

// const BASE_URL = "http://localhost/your-project/api";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUserAuth();
  const from = location.state?.from || "/";

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showResetBox, setShowResetBox] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.email.trim()) return setError("Email is required");
    if (!form.password.trim()) return setError("Password is required");
    if (!/\S+@\S+\.\S+/.test(form.email))
      return setError("Invalid email format");

    setSubmitting(true);
    setError("");
    try {
      const json = await apiFetch(`${process.env.REACT_APP_LOGIN_PATIENT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { email: form.email.trim(), password: form.password },
      });
      // const json = await res.json();
      if (json.status) {
        login({
          patient_id: json.patient_id,
          name: json.name,
          email: form.email,
        });

        navigate(from, { replace: true });
      } else {
        setError(json.message || "Invalid email or password");
      }
    } catch (err) {
      
      setError("Network error. Please try again.");
    }
    setSubmitting(false);
  };
  const handleForgotPassword = async () => {
    const email = form.email.trim();

    if (!email) {
      return setError("Enter registered email first");
    }
    setResetEmail(email)
    setForgotLoading(true);
    setError("");

    try {
      const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_RESET_PASSWORD}`;

      const res = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ EMAIL: email }),
      });

      const json = await res.json();

      

      if (json?.res === "Success" || json?.status === true) {
        setResetEmail(email);
        setShowResetBox(true);
        setError("OTP sent to your email");
      } else {
        setError(json?.message || json?.msg || "Failed to send reset email");
      }
    } catch (err) {
      
      setError("Network error. Please try again.");
    }

    setForgotLoading(false);
  };
  const handleVerifyOTP = async () => {
    setLoading(true);

    try {
      const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_RESET_PASSWORD_WITH_OTP}`;
      
      const res = await fetch(path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          EMAIL: resetEmail,
          OTP: otp,
          "New Password": newPassword,
        }),
      });

      const data = await res.json();
      
      if (data.res === "success" || data.res === "Success") {
        setError("Password Reset Success");
        setShowResetBox(false);
        setOtp("");
        setNewPassword("");
      } else {
        setError("Invalid OTP");
      }
    } catch (err){
      setError("Server Error");
    
    }

    setLoading(false);
  };
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0A1628 0%, #1B2A6B 55%, #0A1628 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Nunito','Segoe UI',sans-serif",
        padding: "1rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background decoration */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: "rgba(29,158,117,.08)",
            top: -100,
            right: -80,
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 250,
            height: 250,
            borderRadius: "50%",
            background: "rgba(55,138,221,.06)",
            bottom: -60,
            left: -60,
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            border: "1px solid rgba(29,158,117,.12)",
            top: "30%",
            left: "5%",
          }}
        />
      </div>

      <div
        style={{
          background: "rgba(255,255,255,.97)",
          borderRadius: 24,
          padding: "40px 44px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 32px 80px rgba(0,0,0,0.35)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "rgba(29,158,117,.1)",
              border: "2px solid rgba(29,158,117,.3)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 12,
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C9 2 6.5 4.2 6.5 7C6.5 8.6 7.1 9.7 7.1 11.2C7.1 13.8 6 16.2 6 18.6C6 20.6 7 22 8.5 22C10 22 10.5 20 12 20C13.5 20 14 22 15.5 22C17 22 18 20.6 18 18.6C18 16.2 16.9 13.8 16.9 11.2C16.9 9.7 17.5 8.6 17.5 7C17.5 4.2 15 2 12 2Z"
                fill="#1D9E75"
              />
              <path
                d="M9.5 7.5C9.5 6 10.6 5 12 5C13.4 5 14.5 6 14.5 7.5"
                stroke="white"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#0A1628",
              margin: "0 0 4px",
              letterSpacing: "-0.3px",
            }}
          >
            Welcome Back
          </h1>
          <p style={{ fontSize: 13, color: "#6B7A8D", margin: 0 }}>
            Sign in to your BrightSmile account
          </p>
        </div>

        {/* Redirect notice */}
        {from !== "/" && (
          <div
            style={{
              background: "#FAEEDA",
              border: "1px solid #F5C4B3",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 20,
              fontSize: 12,
              color: "#633806",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span>🔒</span> Please login to book your appointment
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              background: "#FCEBEB",
              border: "1px solid #F09595",
              borderRadius: 10,
              padding: "10px 14px",
              marginBottom: 18,
              fontSize: 13,
              color: "#501313",
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0A1628",
                display: "block",
                marginBottom: 6,
                letterSpacing: ".3px",
              }}
            >
              EMAIL ADDRESS
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Enter your email"
              autoComplete="email"
              style={{
                width: "100%",
                padding: "11px 14px",
                borderRadius: 10,
                border: "1.5px solid #E4EAF0",
                fontSize: 14,
                outline: "none",
                transition: "border .2s",
                background: "#F8FAFC",
                color: "#0A1628",
                fontFamily: "inherit",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#1D9E75";
                e.target.style.background = "#fff";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E4EAF0";
                e.target.style.background = "#F8FAFC";
              }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#0A1628",
                display: "block",
                marginBottom: 6,
                letterSpacing: ".3px",
              }}
            >
              PASSWORD
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showPass ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Enter your password"
                autoComplete="current-password"
                style={{
                  width: "100%",
                  padding: "11px 44px 11px 14px",
                  borderRadius: 10,
                  border: "1.5px solid #E4EAF0",
                  fontSize: 14,
                  outline: "none",
                  transition: "border .2s",
                  background: "#F8FAFC",
                  color: "#0A1628",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#1D9E75";
                  e.target.style.background = "#fff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E4EAF0";
                  e.target.style.background = "#F8FAFC";
                }}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 16,
                  color: "#6B7A8D",
                }}
              >
                {showPass ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: "100%",
              padding: "13px",
              background: "#1D9E75",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1,
              transition: "all .2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.background = "#0F6E56";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#1D9E75";
            }}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Signing in...
              </>
            ) : (
              "Sign In →"
            )}
          </button>
        </form>
        <div style={{ textAlign: "right", marginTop: 8, marginBottom: 18 }}>
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={forgotLoading}
            style={{
              background: "none",
              border: "none",
              color: "#1D9E75",
              fontSize: 13,
              cursor: "pointer",
              padding: 0,
              fontWeight: 700,
            }}
          >
            {forgotLoading ? "Sending..." : "Forgot Password?"}
          </button>
        </div>
        {/* Divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            margin: "22px 0",
          }}
        >
          <div style={{ flex: 1, height: 1, background: "#E4EAF0" }} />
          <span style={{ fontSize: 12, color: "#9BA8B5" }}>
            New to BrightSmile?
          </span>
          <div style={{ flex: 1, height: 1, background: "#E4EAF0" }} />
        </div>

        {/* Register link */}
        <Link
          to="/register"
          style={{
            display: "block",
            width: "100%",
            padding: "12px",
            textAlign: "center",
            background: "transparent",
            color: "#1D9E75",
            border: "2px solid #1D9E75",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            textDecoration: "none",
            transition: "all .2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#1D9E75";
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "#1D9E75";
          }}
        >
          Create New Account
        </Link>

        {/* Back to home */}
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Link
            to="/"
            style={{ fontSize: 12, color: "#6B7A8D", textDecoration: "none" }}
          >
            ← Back to Home
          </Link>
        </div>
      </div>
      {showResetBox && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              width: "100%",
              maxWidth: 400,
              borderRadius: 18,
              padding: 24,
              boxShadow: "0 20px 60px rgba(0,0,0,.25)",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#0A1628" }}>Reset Password</h3>

            <input
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              style={{
                width: "100%",
                padding: 12,
                marginBottom: 12,
                borderRadius: 10,
                border: "1px solid #ddd",
              }}
            />

            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: "100%",
                padding: 12,
                marginBottom: 16,
                borderRadius: 10,
                border: "1px solid #ddd",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button
                onClick={() => setShowResetBox(false)}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #ddd",
                  background: "#fff",
                }}
              >
                Cancel
              </button>

              <button
                onClick={handleVerifyOTP}
                disabled={loading}
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#1D9E75",
                  color: "#fff",
                }}
              >
                {loading ? "Please wait..." : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
